import { createTheme } from '@mui/material/styles';

// Single source of truth for the app theme.
// Edit colors/shape/typography here to affect the whole application.
export function createAppTheme(overrides = {}) {
  const {
    palette: paletteOverrides = {},
    shape: shapeOverrides = {},
    typography: typographyOverrides = {},
    components: componentsOverrides = {},
    ...rest
  } = overrides;

  return createTheme({
    palette: {
      mode: process.env.REACT_APP_THEME_MODE || 'light',
      primary: {
        main: '#FFD700', // primaryGovt
        light: '#FFE44D',
        dark: '#CCAC00',
        contrastText: '#11181C',
      },
      secondary: {
        main: '#4B0082', // focusDeep
        light: '#6F2B9E',
        dark: '#300054',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#50C878', // successLogic
        light: '#7ED99B',
        dark: '#3B9B5C',
        contrastText: '#FFFFFF',
      },
      background: {
        default: '#F7F8FA', // Matching mobile safe area background
        paper: '#FFFFFF',
      },
      text: {
        primary: '#11181C',
        secondary: '#687076',
      },
      divider: 'rgba(226, 232, 240, 0.9)',
      ...paletteOverrides,
    },
    shape: { 
      borderRadius: 12, // Align with mobile softer radius
      ...shapeOverrides 
    },
    typography: {
      fontFamily: '"Roboto", "Outfit", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontSize: 15,
      h1: { fontSize: '3rem', fontWeight: 800, letterSpacing: '-0.02em', color: '#11181C' },
      h2: { fontSize: '2.25rem', fontWeight: 800, letterSpacing: '-0.01em', color: '#11181C' },
      h3: { fontSize: '1.875rem', fontWeight: 700, color: '#11181C' },
      h4: { fontSize: '1.5rem', fontWeight: 700, color: '#11181C' },
      h5: { fontSize: '1.25rem', fontWeight: 600, color: '#11181C' },
      h6: { fontSize: '1.125rem', fontWeight: 600, color: '#11181C' },
      body1: { fontSize: '1rem', lineHeight: 1.7 },
      body2: { fontSize: '0.9375rem', lineHeight: 1.6 },
      button: { fontSize: '0.95rem', fontWeight: 700, textTransform: 'none' },
      caption: { fontSize: '0.75rem', fontWeight: 500 },
      ...typographyOverrides,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            padding: '10px 20px',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0 6px 20px rgba(255, 215, 0, 0.3)',
            },
          },
          containedPrimary: {
            background: '#FFD700',
            color: '#11181C',
            '&:hover': {
              background: '#F0CA00',
            }
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 14,
            boxShadow: '0 10px 30px rgba(15, 23, 42, 0.04)',
            border: '1px solid rgba(226, 232, 240, 0.9)',
            overflow: 'hidden',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 14,
            backgroundImage: 'none',
          },
          elevation1: {
            boxShadow: '0 8px 30px rgba(15, 23, 42, 0.04)',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 16,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 12,
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            borderRadius: 12,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 600,
          },
        },
      },
      MuiAvatar: {
        defaultProps: {
          variant: 'rounded',
        },
        styleOverrides: {
          root: {
            borderRadius: 12,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: 'rgba(255, 255, 255, 0.92)',
            backdropFilter: 'blur(8px)',
            borderBottom: '1px solid rgba(226, 232, 240, 0.95)',
            color: '#11181C',
            borderRadius: 0, // AppBar typically doesn't have border radius in web
            boxShadow: 'none'
          },
        },
      },
      ...componentsOverrides,
    },
    ...rest,
  });
}

const theme = createAppTheme();
export default theme;
